import type { DragEndEvent, UniqueIdentifier } from "@dnd-kit/core"
import { DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core"
import { SortableContext, arrayMove, sortableKeyboardCoordinates, verticalListSortingStrategy } from "@dnd-kit/sortable"
import {
  Box,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material"
import { flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table"
import type { ReactElement, ReactNode } from "react"
import React, { useCallback } from "react"
import { twMerge } from "tailwind-merge"

import { DraggableRowContent } from "@/components/Tables/Aeros/components/DraggableRowContent"
import { TableHeaderCell } from "@/components/Tables/Aeros/components/TableHeaderCell"
import { TableLoadingRow } from "@/components/Tables/Aeros/components/TableLoadingRow"
import { TableNoDataFoundRow } from "@/components/Tables/Aeros/components/TableNoDataFoundRow"
import type { AerosTableProps } from "@/components/Tables/Aeros/types/table"

export const AerosBaseTable = <TData,>({
  error,
  features,
  slotProps,
  noDataMessage = "",
  draggable = false,
  onReorder,
}: AerosTableProps<TData>): ReactElement => {
  const {
    data = [],
    columns = [],
    state,
    getCoreRowModel: customCoreRowModel,
    ...tanstackOptions
  } = features?.tanstackOptions ?? {}

  const [items, setItems] = React.useState<TData[]>(data)
  React.useEffect(() => setItems(data), [data])

  // Produce stable identifiers for DnD & lookup
  const ids: UniqueIdentifier[] = React.useMemo(() => items.map((row, i) => (row as any)?.id ?? String(i)), [items])
  const indexById = React.useCallback((id: UniqueIdentifier) => ids.indexOf(id), [ids])

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event
      if (!over || active.id === over.id) return

      const oldIndex = indexById(active.id)
      const newIndex = indexById(over.id)
      if (oldIndex < 0 || newIndex < 0) return

      const newOrder = arrayMove(items, oldIndex, newIndex)
      setItems(newOrder)
      onReorder?.({
        draggedItemIndex: oldIndex,
        newIndex,
        newOrder,
        setNewOrder: setItems,
      })
    },
    [indexById, items, onReorder]
  )

  const table = useReactTable<TData>({
    data: items,
    columns,
    state,
    getCoreRowModel: customCoreRowModel ?? getCoreRowModel<TData>(),
    ...tanstackOptions,
  })

  const rowModel = table.getRowModel()
  const rowCount = table.getRowCount()

  const expandedState = table.getState().expanded
  const enablePrintMode = features?.table?.enablePrintMode ?? false

  const renderRowDetail = useCallback(
    (content: ReactNode) => {
      if (!content) return null
      return (
        <TableRow className={twMerge("!border-t-0 border-b-0", "hover:bg-transparent", slotProps?.bodyRow?.className)}>
          <TableCell
            colSpan={columns.length}
            className={twMerge("!border-t-0", "p-0", slotProps?.rowDetailCell?.className)}
          >
            <Box className='px-4 py-2'>{content}</Box>
          </TableCell>
        </TableRow>
      )
    },
    [columns.length, slotProps?.bodyRow?.className, slotProps?.rowDetailCell?.className]
  )

  const renderRows = React.useMemo(() => {
    if (rowCount === 0) {
      if (features?.tanstackOptions?.state?.isLoading) return <TableLoadingRow columns={columns} />
      return <TableNoDataFoundRow colSpan={columns.length}>{noDataMessage}</TableNoDataFoundRow>
    }

    const rows = rowModel.rows.map((row) => {
      const rowKey = row.id

      const { getClassName: getRowClassName, ...bodyRowRest } = slotProps?.bodyRow || {}
      const rowProps = {
        ...bodyRowRest,
        onClick: (e: React.MouseEvent) => {
          // keep your click guard
          if (
            ['[data-clickable="true"]', ".MuiBackdrop-root"].some((path) => (e.target as HTMLElement).closest(path))
          ) {
            return
          }
          slotProps?.bodyRow?.onClick?.(row)
        },
        selected: features?.tanstackOptions?.state?.rowSelection && row.getIsSelected(),
        sx: {
          cursor: draggable ? "grab !important" : "default",
          ...(enablePrintMode && {
            "@media print": {
              pageBreakInside: "avoid",
              breakInside: "avoid",
            },
          }),
        },
        className: twMerge(
          !features?.tanstackOptions?.renderRowDetail && "!border-b-0",
          slotProps?.bodyRow?.className,
          getRowClassName ? getRowClassName(row) : ""
        ),
      } as const

      const cells = row.getVisibleCells().map((cell) => {
        const align = cell.column.id === "select" ? "center" : (slotProps?.bodyCell?.align ?? "left")
        const onClick = cell.column.id === "select" ? row.getToggleSelectedHandler() : undefined
        const columnSize = cell.column.getSize()
        const flexibleSizing = features?.table?.flexibleColumnSizing ?? false
        const sizeStyles = flexibleSizing
          ? undefined
          : {
              width: columnSize,
              minWidth: columnSize,
              maxWidth: columnSize,
            }
        const className = twMerge(
          slotProps?.bodyCell?.className,
          row.getIsExpanded() ? slotProps?.bodyCellExpanded?.className : "",
          features?.tanstackOptions?.renderRowDetail && "!border-b-0"
        )
        const isClickable = cell.column.id === "expand" || cell.column.id === "select" || !!onClick

        return (
          <TableCell
            key={cell.id}
            align={align}
            onClick={onClick}
            data-clickable={isClickable}
            sx={sizeStyles}
            className={className}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCell>
        )
      })

      if (draggable) {
        return <DraggableRowContent row={row} rowKey={rowKey} rowProps={rowProps} key={rowKey} />
      }

      const regularRow = (
        <TableRow key={rowKey} {...rowProps}>
          {cells}
        </TableRow>
      )

      // Render expanded content if row is expanded and renderExpandedContent is provided
      if (row.getIsExpanded() && features?.tanstackOptions?.renderExpandedContent) {
        return (
          <React.Fragment key={`expanded-row-content-${rowKey}`}>
            {regularRow}
            <TableRow {...slotProps?.expandedRow} key={`expanded-${rowKey}`}>
              <TableCell
                colSpan={columns.length}
                className='p-0 hover:bg-transparent'
                key={`expanded-${rowKey}-cell`}
                {...slotProps?.expandedCell}
              >
                {features.tanstackOptions.renderExpandedContent(row)}
              </TableCell>
            </TableRow>
          </React.Fragment>
        )
      }

      if (features?.tanstackOptions?.renderRowDetail) {
        return (
          <React.Fragment key={`row-detail-${rowKey}`}>
            {regularRow}
            {renderRowDetail(features?.tanstackOptions?.renderRowDetail({ row }))}
          </React.Fragment>
        )
      }

      return regularRow
    })

    if (draggable) {
      return (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={ids} strategy={verticalListSortingStrategy}>
            {rows}
          </SortableContext>
        </DndContext>
      )
    }

    return rows
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    rowCount,
    rowModel.rows,
    draggable,
    ids,
    features?.tanstackOptions,
    columns,
    noDataMessage,
    slotProps?.bodyRow,
    slotProps?.bodyCell,
    slotProps?.bodyCellExpanded?.className,
    slotProps?.expandedRow,
    slotProps?.expandedCell,
    sensors,
    handleDragEnd,
    expandedState,
    enablePrintMode,
  ])

  const stickyHeader = !enablePrintMode && (features?.table?.stickyHeader ?? true)

  return (
    <TableContainer
      {...slotProps?.container}
      className={twMerge("relative", slotProps?.container?.className)}
      sx={{
        ...slotProps?.container?.sx,
        ...(enablePrintMode && {
          "@media print": {
            overflow: "visible",
            height: "auto",
          },
        }),
      }}
    >
      {features?.tanstackOptions?.state?.isLoading && rowCount !== 0 && (
        <Box className='sticky top-0 z-[60]'>
          <LinearProgress />
        </Box>
      )}
      <Table
        stickyHeader={stickyHeader}
        aria-label='sticky table'
        sx={{
          ...(enablePrintMode && {
            "@media print": {
              pageBreakInside: "auto",
            },
          }),
        }}
      >
        <TableHead
          sx={{
            ...(enablePrintMode && {
              "@media print": {
                display: "table-header-group",
              },
            }),
          }}
        >
          <TableRow {...slotProps?.headerRow}>
            {table.getFlatHeaders().map((header) => (
              <TableHeaderCell
                key={header.id}
                header={header}
                className={slotProps?.headerCell?.className}
                flexibleSizing={features?.table?.flexibleColumnSizing ?? false}
              />
            ))}
          </TableRow>
        </TableHead>
        <TableBody>{renderRows}</TableBody>
      </Table>
      {error && <Typography color='error'>{error.message}</Typography>}
    </TableContainer>
  )
}
